import { useMemo, useState } from 'react';
import { Button } from '@habitech/call-to-action';
import { TextField } from '@habitech/keychain-atoms-inputs';
import {
    ExpirationMonthGroup,
    ExpirationMonthsStack,
    ExpirationMonthTitle,
    InfoUser,
    InfoUserItems,
    InfoUserSection,
    InfoUserSectionHeader,
    InfoUserSectionTitle,
    Wrapper,
    WrapperIcon,
} from '../styles';
import { summarizeActiveProductsByExpiration } from 'src/helpers/activeProductsByExpiration';
import { useSuggestedForm } from './suggested.hook';
import { GridContainer, GridItem } from 'src/components/common/grids';
import { selectDataSuggested } from 'src/redux/shopping_cart/shopping_cart.selectors';
import { useAppSelector } from 'src/hooks/redux';
import { WrapperButton } from './suggested-form.styles';
import Icon from 'src/components/common/Icon';
import AccordionComponent from 'src/components/common/Accordion/Accordion';
import Modal from 'src/components/common/modal/Modal';
import UserProfileEditModal from './UserProfileEditModal';

export const EMPTY_EXPIRING_PRODUCTS_COPY =
    'No hay productos por vencer en los próximos 90 días.';

const SuggestedForm = () => {
    const [showModal, setShowModal] = useState(false);
    const { handleChange, handleSubmit, isValid, errors } = useSuggestedForm();
    const {loading, errorMessage, suggested} = useAppSelector(selectDataSuggested);

    const expirationSummaries = useMemo(
        () => summarizeActiveProductsByExpiration(suggested.active_products_by_expiration_month),
        [suggested.active_products_by_expiration_month]
    );

    const eliteCartProduct9 =
        suggested.user_profile?.users_delivery_leads_periods_cart_product_9 ??
        suggested.user_profile?.freemium_state?.users_delivery_leads_periods_cart_product_9;

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (isValid) handleSubmit();
    };

    return (
        <Wrapper>
            <form onSubmit={onSubmit} noValidate>
            <GridContainer>
                <GridItem lg={10}>
                    <WrapperIcon>
                        <Icon icon="pcom-icon-user" size={18} />
                        Buscar del cliente
                    </WrapperIcon>
                    <TextField
                        originType="pcom"
                        name="email"
                        dataId="testing_text_field"
                        defaultValue={''}
                        placeHolder="Ingrese correo electrónico"
                        onChange={handleChange.bind(null)}
                        error={errors["email"] || errorMessage}
                        errorMessage={errors["email"] || errorMessage}
                    />
                </GridItem>
                <GridItem lg={2}>
                    <WrapperButton>
                        <Button
                            size='large'
                            disabled={!isValid}
                            dataId={'button-send-suggestions'}
                            originType="pcom"
                            variant="primary"
                            loading={loading}
                            type="submit"
                        >
                            Buscar
                        </Button>
                    </WrapperButton>
                </GridItem>
                {
                    suggested.users.email !== "" &&
                    <GridItem lg={12}>
                        <AccordionComponent title="Información del usuario">
                        <InfoUser>
                            {/* 1. Contacto básico */}
                            <InfoUserSection>
                                <InfoUserSectionHeader>
                                    <InfoUserSectionTitle>Contacto básico</InfoUserSectionTitle>
                                    <Button
                                        type="button"
                                        size="medium"
                                        variant="stroke"
                                        originType="pcom"
                                        dataId="button-open-modal"
                                        onClick={() => setShowModal(true)}
                                    >
                                        Editar perfil
                                    </Button>
                                </InfoUserSectionHeader>
                                <InfoUserItems>
                                    <div className="item-user-info">
                                        #id: {suggested.users.user_id}
                                    </div>
                                    {suggested.user_profile?.name && (
                                        <div className="item-user-info">
                                            Nombre: {suggested.user_profile.name} {suggested.user_profile.lastname}
                                        </div>
                                    )}
                                    <div className="item-user-info">
                                        <Icon icon="pcom-icon-user" size={18} /> {suggested.users.email}
                                    </div>
                                    {suggested.user_profile?.phone_contact && (
                                        <div className="item-user-info">
                                            Tel contacto: {suggested.user_profile.phone_contact}
                                        </div>
                                    )}
                                    {suggested.user_profile?.phone_whatsapp && (
                                        <div className="item-user-info">
                                            Tel WhatsApp: {suggested.user_profile.phone_whatsapp}
                                        </div>
                                    )}
                                    {suggested.user_profile?.feed_name != null && suggested.user_profile?.from_xml != null && (
                                        <div className="item-user-info">
                                            Es CRM: {suggested.user_profile.from_xml === 1 && suggested.user_profile.feed_name !== '' ? 'Sí' : 'No'}
                                        </div>
                                    )}
                                </InfoUserItems>
                            </InfoUserSection>

                            {/* 2. Propiedades y estados en PCOM */}
                            <InfoUserSection>
                                <InfoUserSectionTitle>Propiedades y estados en PCOM</InfoUserSectionTitle>
                                <InfoUserItems>
                                    <div className="item-user-info" data-poc={suggested.user_profile?.freemium_state !== null}>
                                        POC Premium: {suggested.user_profile?.freemium_state !== null ? 'Sí' : 'No'}
                                    </div>
                                    <div className="item-user-info">
                                        POC Elite: {(eliteCartProduct9?.length ?? 0) > 0 ? 'Sí' : 'No'}
                                    </div>
                                    {suggested.user_profile?.broker_type && (
                                        <div className="item-user-info">
                                            Tipo broker: {suggested.user_profile.broker_type}
                                        </div>
                                    )}
                                    <div className="item-user-info">
                                        Propiedades activas: {suggested.user_profile?.active_properties ?? 0}
                                    </div>
                                    <div className="item-user-info">
                                        Propiedades destacadas: {suggested.user_profile?.highlighted_properties ?? 0}
                                    </div>
                                    <div className="item-user-info">
                                        Slots activos: {suggested.user_profile?.total_active_slots ?? 0}
                                    </div>
                                </InfoUserItems>
                            </InfoUserSection>

                            {/* 3. Productos a expirar (solo name_product: Slot destacado, Slot, Elite, Premium) */}
                            <InfoUserSection>
                                <InfoUserSectionTitle>Productos a expirar</InfoUserSectionTitle>
                                {expirationSummaries.length === 0 ? (
                                    <InfoUserItems>
                                        <div className="item-user-info">
                                            {EMPTY_EXPIRING_PRODUCTS_COPY}
                                        </div>
                                    </InfoUserItems>
                                ) : (
                                    <ExpirationMonthsStack>
                                        {expirationSummaries.map(({ monthKey, monthLabel, rows }) => (
                                            <ExpirationMonthGroup key={monthKey}>
                                                <ExpirationMonthTitle>{monthLabel}</ExpirationMonthTitle>
                                                <InfoUserItems>
                                                    {rows.map(({ label, count }) => (
                                                        <div key={label} className="item-user-info">
                                                            {label}: {count}
                                                        </div>
                                                    ))}
                                                </InfoUserItems>
                                            </ExpirationMonthGroup>
                                        ))}
                                    </ExpirationMonthsStack>
                                )}
                            </InfoUserSection>
                        </InfoUser>
                        </AccordionComponent>
                    </GridItem>
                }
            </GridContainer>
            </form>
            {showModal && suggested.users.email !== '' && (
                <Modal onClose={() => setShowModal(false)}>
                    <UserProfileEditModal onClose={() => setShowModal(false)} />
                </Modal>
            )}
        </Wrapper>
    );
};
export default SuggestedForm;
